import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, TYPE_META } from "@/lib/api";
import { toast } from "sonner";
import { X, Paperclip, Search as SearchIcon } from "lucide-react";

/**
 * WritePostModal — freeform text post, optionally attaching one library
 * item. Instagram-style compose: big textarea (up to 1000 chars) + a
 * lightweight attach picker that pulls from /api/items with debounced
 * search. Posts either standalone (item_id null) or with a snapshot.
 */
const MAX = 1000;

export default function WritePostModal({ onClose, onPosted }) {
  const [body, setBody] = useState("");
  const [attached, setAttached] = useState(null);          // { id, title, creator, type, image_url }
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQ, setPickerQ] = useState("");
  const [pickerResults, setPickerResults] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const bodyRef = useRef(null);
  const searchTimer = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => bodyRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") pickerOpen ? setPickerOpen(false) : onClose?.(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, pickerOpen]);

  // Hide the global Add FAB while the modal is open.
  useEffect(() => {
    document.body.classList.add("modal-open");
    return () => document.body.classList.remove("modal-open");
  }, []);

  // Debounced item search — only fires while picker is open.
  useEffect(() => {
    if (!pickerOpen) return;
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      setPickerLoading(true);
      try {
        // /items returns a plain array (no pagination envelope). Cap to 30
        // client-side so the picker stays snappy.
        const r = await api.get("/items", { params: pickerQ ? { q: pickerQ } : {} });
        const rows = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        setPickerResults(rows.slice(0, 30));
      } catch { setPickerResults([]); }
      finally { setPickerLoading(false); }
    }, 220);
    return () => clearTimeout(searchTimer.current);
  }, [pickerQ, pickerOpen]);

  async function send() {
    if (sending) return;
    const trimmed = body.trim().slice(0, MAX);
    if (!trimmed && !attached) {
      toast.error("write something or attach an item");
      return;
    }
    setSending(true);
    try {
      await api.post("/commons/posts", {
        item_id: attached?.id || null,
        note: trimmed,
      });
      toast.success("posted");
      onPosted?.();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "could not post";
      toast.error(detail);
      if (status === 400 && typeof detail === "string" && detail.toLowerCase().includes("username")) {
        navigate("/commons");
        onClose?.();
      }
    } finally {
      setSending(false);
    }
  }

  const remaining = MAX - body.length;
  const over = remaining < 0;
  const disabled = sending || over || (!body.trim() && !attached);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8 bg-[rgba(0,0,0,0.7)] backdrop-blur-sm"
      onClick={onClose}
      data-testid="write-post-modal-backdrop"
    >
      <div
        className="w-full max-w-lg placed-card p-6 relative"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        data-testid="write-post-modal"
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="write-post-close"
          className="absolute top-3 right-3 text-[var(--cream-mute)] hover:text-[var(--brass)] transition-colors"
          aria-label="close"
        >
          <X size={16} strokeWidth={1.5} />
        </button>

        <p className="font-mono-lc text-[10px] tracking-[0.24em] uppercase text-[var(--brass)] opacity-80">
          write a post
        </p>
        <h2 className="mt-2 font-serif-lit text-2xl text-[var(--cream)]">
          what&apos;s on your mind?
        </h2>

        <textarea
          ref={bodyRef}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="say anything — a thought, a recommendation, a passing note…"
          rows={6}
          data-testid="write-post-body"
          className="mt-4 w-full bg-[var(--bg)] border border-[rgba(179,137,90,0.24)] rounded p-3 font-garamond italic text-[var(--cream)] placeholder:text-[var(--cream-mute)] focus:outline-none focus:border-[var(--brass)] resize-none"
        />
        <div className="mt-1 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            data-testid="write-post-attach-btn"
            className="inline-flex items-center gap-1.5 font-mono-lc text-[10px] tracking-[0.16em] uppercase text-[var(--cream-mute)] hover:text-[var(--brass)] transition-colors"
          >
            <Paperclip size={11} strokeWidth={1.6} />
            {attached ? "change attachment" : "attach a library item"}
          </button>
          <p
            className={`font-mono-lc text-[10px] tracking-[0.14em] ${over ? "text-[#C86A5C]" : "text-[var(--cream-mute)]"}`}
            data-testid="write-post-counter"
          >
            {remaining} left
          </p>
        </div>

        {attached && (
          <div className="mt-4 placed-card p-3 flex items-center gap-3" data-testid="write-post-attached">
            {attached.image_url && (
              <img src={attached.image_url} alt="" className="w-14 h-14 object-cover rounded-[2px] border border-[rgba(179,137,90,0.24)]" />
            )}
            <div className="flex-1 min-w-0">
              <p className="font-mono-lc text-[10px] tracking-[0.14em] uppercase text-[var(--brass)] opacity-80">
                {TYPE_META[attached.type]?.label?.toLowerCase() || attached.type}
              </p>
              <p className="font-serif-lit text-[var(--cream)] text-base truncate">{attached.title || "untitled"}</p>
              {attached.creator && (
                <p className="font-garamond italic text-[var(--cream-dim)] text-sm truncate">by {attached.creator}</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => setAttached(null)}
              data-testid="write-post-detach"
              className="text-[var(--cream-mute)] hover:text-[var(--brass)]"
              aria-label="remove attachment"
            >
              <X size={14} strokeWidth={1.5} />
            </button>
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={onClose} data-testid="write-post-cancel" className="lc-btn-ghost">
            never mind
          </button>
          <button
            type="button"
            onClick={send}
            disabled={disabled}
            data-testid="write-post-send"
            className="lc-btn"
          >
            {sending ? "posting…" : "post"}
          </button>
        </div>

        {pickerOpen && (
          <ItemPicker
            q={pickerQ}
            onQChange={setPickerQ}
            results={pickerResults}
            loading={pickerLoading}
            onPick={(it) => { setAttached(it); setPickerOpen(false); setPickerQ(""); }}
            onClose={() => setPickerOpen(false)}
          />
        )}
      </div>
    </div>
  );
}

function ItemPicker({ q, onQChange, results, loading, onPick, onClose }) {
  const grouped = useMemo(() => results.slice(0, 30), [results]);
  return (
    <div
      className="absolute inset-0 rounded-[inherit] bg-[var(--bg-panel)] p-5 flex flex-col"
      onClick={(e) => e.stopPropagation()}
      data-testid="write-post-picker"
    >
      <div className="flex items-center gap-3">
        <div className="flex-1 relative">
          <input
            type="search"
            autoFocus
            value={q}
            onChange={(e) => onQChange(e.target.value)}
            placeholder="search your library…"
            data-testid="write-post-picker-search"
            className="w-full bg-[var(--bg)] border border-[rgba(179,137,90,0.24)] rounded pl-9 pr-3 py-2 font-garamond italic text-[var(--cream)] placeholder:text-[var(--cream-mute)] focus:outline-none focus:border-[var(--brass)]"
          />
          <SearchIcon size={14} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--cream-mute)]" />
        </div>
        <button type="button" onClick={onClose} className="lc-btn-ghost" data-testid="write-post-picker-close">
          close
        </button>
      </div>
      <div className="mt-4 flex-1 overflow-auto -mr-2 pr-2" data-testid="write-post-picker-results">
        {loading ? (
          <p className="text-center py-8 font-mono-lc text-[10px] tracking-[0.24em] uppercase text-[var(--cream-mute)]">
            looking…
          </p>
        ) : grouped.length === 0 ? (
          <p className="text-center py-8 font-garamond italic text-[var(--cream-dim)]">
            nothing to attach yet.
          </p>
        ) : (
          <ul className="space-y-2">
            {grouped.map(it => (
              <li key={it.id}>
                <button
                  type="button"
                  onClick={() => onPick(it)}
                  data-testid={`write-post-picker-item-${it.id}`}
                  className="w-full text-left placed-card p-3 flex items-center gap-3 hover:border-[var(--brass)]"
                >
                  {it.image_url ? (
                    <img src={it.image_url} alt="" className="w-10 h-10 object-cover rounded-[2px] border border-[rgba(179,137,90,0.2)]" />
                  ) : (
                    <div className="w-10 h-10 rounded-[2px] bg-[var(--bg-panel-hi)]" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-serif-lit text-[var(--cream)] text-sm truncate">{it.title || "untitled"}</p>
                    <p className="font-mono-lc text-[10px] tracking-[0.14em] text-[var(--cream-mute)] truncate">
                      {TYPE_META[it.type]?.label?.toLowerCase() || it.type}{it.creator ? ` · ${it.creator}` : ""}
                    </p>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
